import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config';
import './Login.css';
import ClerkLoginPanel from '../components/ClerkLoginPanel';
import LoginBrandCollage from '../components/LoginBrandCollage';
import ThemeToggle from '../components/ThemeToggle';
import { useBrandIdentity } from '../utils/brandIdentity';

function Login({ onNavigate, theme = 'dark', onToggleTheme }) {
  // A company's sign-in link (risklo.io/b/<slug>) shows that company here.
  const brand = useBrandIdentity();
  const SHOW_WHOP_LOGIN_OPTION = false;
  const {
    user,
    signInWithEmail,
    registerWithEmail,
    verifyEmail,
    resendVerificationEmail,
    forgotPassword,
    resetPassword,
    signInWithMicrosoft,
    authError,
    setAuthError,
  } = useAuth();

  const [isCreateMode, setIsCreateMode] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  // Escape hatch out of Clerk sign-in (misconfigured Clerk, or a Clerk email
  // that differs from the account's Whop email) — prevents a hard lockout.
  const [useLegacySignIn, setUseLegacySignIn] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && onNavigate) {
      onNavigate('dashboard');
    }
  }, [user, onNavigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const verifyToken = String(params.get('verifyToken') || '').trim();
    const incomingResetToken = String(params.get('resetToken') || '').trim();

    if (verifyToken) {
      setSubmitting(true);
      setAuthError(null);
      verifyEmail(verifyToken)
        .then(() => {
          setInfoMessage('Email verified successfully. You can now sign in.');
        })
        .catch((error) => {
          setAuthError(error.message || 'Verification link is invalid or expired.');
        })
        .finally(() => {
          setSubmitting(false);
        });
    }

    if (incomingResetToken) {
      setResetToken(incomingResetToken);
      setShowForgotPassword(false);
      setIsCreateMode(false);
      setInfoMessage('');
    }

    if (verifyToken || incomingResetToken) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [verifyEmail, setAuthError]);

  const submitEmailAuth = async () => {
    try {
      setSubmitting(true);
      setAuthError(null);
      setInfoMessage('');

      if (resetToken) {
        if (password.length < 8) {
          throw new Error('Password must be at least 8 characters');
        }
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }
        await resetPassword(resetToken, password);
        setResetToken('');
        setPassword('');
        setConfirmPassword('');
        setInfoMessage('Password updated. You can now sign in.');
      } else if (showForgotPassword) {
        await forgotPassword(email);
        setInfoMessage('If that account exists, a password reset link has been sent.');
      } else {
        if (isCreateMode) {
          const result = await registerWithEmail(name, email, password);
          if (result?.requiresEmailVerification) {
            setPendingVerificationEmail(result.email || email);
            setIsCreateMode(false);
            setPassword('');
            setInfoMessage(result.message || 'Account created. Please verify your email. Check your spam folder if you don\'t see it.');
            return;
          }
        } else {
          await signInWithEmail(email, password);
        }
        if (onNavigate) onNavigate('dashboard');
      }
    } catch (error) {
      setAuthError(error.message || 'Authentication failed');
      if (error?.code === 'EMAIL_NOT_VERIFIED') {
        setPendingVerificationEmail(error?.email || email);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    const targetEmail = pendingVerificationEmail || email;
    if (!targetEmail) {
      setAuthError('Enter your email first to resend verification.');
      return;
    }
    try {
      setSubmitting(true);
      setAuthError(null);
      await resendVerificationEmail(targetEmail);
      setInfoMessage('If that account exists, a verification email has been sent. Check your spam folder if you don\'t see it.');
    } catch (error) {
      setAuthError(error.message || 'Failed to resend verification email');
    } finally {
      setSubmitting(false);
    }
  };

  const startGoogle = () => {
    const frontendUrl = window.location.origin;
    const oauthUrl = API_ENDPOINTS.authGoogleStart(frontendUrl);
    window.location.href = oauthUrl;
  };

  const startWhop = () => {
    const frontendUrl = window.location.origin;
    const oauthUrl = API_ENDPOINTS.authWhopStart(frontendUrl);
    window.location.href = oauthUrl;
  };

  const MicrosoftIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="2" y="2" width="9" height="9" fill="#f25022" />
      <rect x="13" y="2" width="9" height="9" fill="#7fba00" />
      <rect x="2" y="13" width="9" height="9" fill="#00a4ef" />
      <rect x="13" y="13" width="9" height="9" fill="#ffb900" />
    </svg>
  );

  const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <g fillRule="evenodd">
        <path d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z" fill="#EA4335"/>
        <path d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.21 1.18-.84 2.18-1.79 2.85l2.75 2.13c1.66-1.52 2.72-3.76 2.72-6.48z" fill="#4285F4"/>
        <path d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9.008 9.008 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z" fill="#FBBC05"/>
        <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.75-2.13c-.76.53-1.78.9-3.21.9-2.38 0-4.4-1.57-5.12-3.74L.96 13.04C2.45 15.98 5.48 18 9 18z" fill="#34A853"/>
      </g>
    </svg>
  );

  // Clerk sign-in is OFF for now.
  //
  // Google's OAuth endpoint skips the account chooser whenever it can resolve
  // the account on its own, and Clerk's prebuilt <SignIn /> has no way to send
  // `prompt=select_account` — that parameter (`oidcPrompt`) exists only on the
  // custom `authenticateWithRedirect` flow, which would mean replacing Clerk's
  // social buttons with our own. So RiskLo's own sign-in owns the page again.
  //
  // Nothing else is removed: flip this to true and Clerk's card comes back,
  // provided REACT_APP_CLERK_PUBLISHABLE_KEY is set.
  const SHOW_CLERK_SIGN_IN = false;

  const clerkEnabled = SHOW_CLERK_SIGN_IN
    && Boolean(process.env.REACT_APP_CLERK_PUBLISHABLE_KEY);
  const showLegacyForm = (!clerkEnabled || useLegacySignIn)
    || Boolean(resetToken) || showForgotPassword;

  // Clerk's card is rendered bare — no RiskLo logo/heading/help-box wrapper.
  // Nesting it inside .login-card produced a cramped card-in-a-card; Clerk's
  // hosted portal is a standalone card and that is what we mirror.
  const showClerkCard = clerkEnabled && !useLegacySignIn && !resetToken && !showForgotPassword;

  const clerkCard = (
    <>
      <ClerkLoginPanel />
      <button
        type="button"
        className="login-legacy-toggle"
        onClick={() => setUseLegacySignIn(true)}
      >
        Trouble signing in? Use email and password
      </button>
      {infoMessage ? <div className="login-info">{infoMessage}</div> : null}
      {authError ? <div className="login-error">{authError}</div> : null}
    </>
  );

  // RiskLo's own form. Unchanged from what it always was — it just sits in the
  // split layout's left column now instead of a centered page of its own, so the
  // brand panel stays whichever sign-in is showing.
  const riskloCard = (
    <div className="login-card">
      <div className="login-mark-wrap">
        <img
          src={brand.logoUrl}
          alt={`${brand.appName} logo`}
          className="login-mark-image"
        />
      </div>
      <h2 className="login-title">
        {resetToken ? (
          'Set a new password'
        ) : showForgotPassword ? (
          'Reset your password'
        ) : isCreateMode ? (
          'Create password for your approved email'
        ) : (
          <>
            Sign in to <span className="login-title-brand">{brand.appName}{brand.isPlatform ? <sup className="reg-mark">®</sup> : null}</span>
          </>
        )}
      </h2>
      {brand.poweredBy && !resetToken && !showForgotPassword ? (
        <div className="login-powered-by">Powered by <strong>RiskLo</strong></div>
      ) : null}
      {/* Starter / Brand plans: RiskLo is the app, the company is the workspace. */}
      {brand.workspace && !resetToken && !showForgotPassword ? (
        <div className="login-workspace">
          {brand.workspace.logoUrl ? <img src={brand.workspace.logoUrl} alt="" className="login-workspace-logo" /> : null}
          <span>{brand.workspace.name}</span>
        </div>
      ) : null}

      {!resetToken && !showForgotPassword && (
        <div className="login-help-box">
          Use your Whop membership email.
        </div>
      )}

      {(!clerkEnabled || useLegacySignIn) && !resetToken && !showForgotPassword && (
        <>
          {SHOW_WHOP_LOGIN_OPTION && (
            <button className="whop-primary-btn" onClick={startWhop} title="Continue with Whop">
              Continue with Whop
            </button>
          )}
          <div className="login-secondary-caption">Preferred sign-in options (recommended)</div>
          <div className="provider-row">
            <button className="provider-btn" onClick={startGoogle} title="Continue with Google">
              <GoogleIcon />
              <span>Google</span>
            </button>
            <button className="provider-btn" onClick={signInWithMicrosoft} title="Continue with Microsoft">
              <MicrosoftIcon />
              <span>Microsoft</span>
            </button>
          </div>
          <div className="login-divider"><span>Or use email</span></div>
          {isCreateMode && (
            <div className="login-email-warning">
              Use your exact Whop membership email (same spelling and inbox).
            </div>
          )}
        </>
      )}

      {showLegacyForm && isCreateMode && !showForgotPassword && !resetToken && (
        <input
          className="login-input"
          type="text"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      )}

      {showLegacyForm && !resetToken && (
        <input
          className="login-input"
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      )}

      {showLegacyForm && !showForgotPassword && (
        <input
          className="login-input"
          type="password"
          placeholder={resetToken ? 'New password' : 'Password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      )}

      {resetToken && (
        <input
          className="login-input"
          type="password"
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
      )}

      {showLegacyForm ? (
      <button className="login-continue-btn" disabled={submitting} onClick={submitEmailAuth}>
        {submitting ? 'Please wait...' : (
          resetToken
            ? 'Update Password'
            : showForgotPassword
              ? 'Send Reset Link'
              : (isCreateMode ? 'Create Account' : 'Continue')
        )}
      </button>
      ) : null}

      {showLegacyForm && !resetToken && !showForgotPassword && (
        <div className="login-links-row">
          <button
            className="login-text-link"
            onClick={() => {
              setShowForgotPassword(true);
              setIsCreateMode(false);
              setPassword('');
              setAuthError(null);
              setInfoMessage('');
            }}
          >
            Forgot password?
          </button>
          <button
            className={`login-mode-toggle ${!isCreateMode ? 'login-first-time-setup-btn' : ''}`}
            onClick={() => {
              setIsCreateMode(!isCreateMode);
              setAuthError(null);
              setInfoMessage('');
            }}
          >
            {isCreateMode ? 'Back to sign in' : 'New here? First-time setup'}
          </button>
        </div>
      )}

      {(resetToken || showForgotPassword) && (
        <button
          className="login-mode-toggle"
          onClick={() => {
            if (resetToken) {
              setResetToken('');
              setPassword('');
              setConfirmPassword('');
            } else {
              setShowForgotPassword(false);
            }
            setAuthError(null);
            setInfoMessage('');
          }}
        >
          Back to sign in
        </button>
      )}

      {pendingVerificationEmail && !user ? (
        <button
          className="login-secondary-btn"
          disabled={submitting}
          onClick={handleResendVerification}
        >
          Resend verification email
        </button>
      ) : null}

      {infoMessage ? <div className="login-info">{infoMessage}</div> : null}
      {authError ? <div className="login-error">{authError}</div> : null}
    </div>
  );

  return (
    <div className="login-split">
      {onToggleTheme ? (
        <div className="login-theme-toggle">
          <ThemeToggle theme={theme} onToggle={onToggleTheme} compact />
        </div>
      ) : null}
      <div className="login-split-form">
        <div className="login-split-form-inner">
          {showClerkCard ? clerkCard : riskloCard}
        </div>
      </div>

      {/* Right: brand panel. Hidden below 900px so the form owns small screens. */}
      <aside className="login-split-brand" aria-hidden="true">
        <div className="login-split-brand-inner">
          <div className="login-split-wordmark">
            {brand.isPlatform ? <>Risk<span>Lo</span><sup className="reg-mark">®</sup></> : brand.appName}
          </div>
          <p className="login-split-tagline">
            {brand.tagline || 'Strategy risk assessment for futures and equity algos.'}
          </p>
          <LoginBrandCollage />
          <p className="login-split-disclaimer">
            Illustrative figures shown for display only — not live results.
          </p>
        </div>
      </aside>
    </div>
  );
}

export default Login;
